import * as yup from "yup";
import dayjs from "dayjs";
import { useForm, useFieldArray, Resolver, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  PrimaryButton,
  TextInput,
  OutlinedButton,
  Select,
} from "@normasteaching/norma-ui";
import { PropsWithChildren, useState, useEffect, useMemo } from "react";
import { NormasTeachingItemType } from "../../Stripe/StripeOrderDTO.ts";
import {
  PromoApplicableProductDTO,
  PromoDTO,
} from "../../ProductService/NormasTeachingPromo/useAllPromos.ts";
import { NormasTeachingProductDTO } from "../../ProductService/NormasTeachingProduct/useNormasTeachingProductById.ts";
import { X, Plus, Trash2, Package } from "lucide-react";
import { matchesItemSearch } from "../../UI/Utils/matchesItemSearch.ts";
import { CreateBonusInput } from "../../ProductService/NormasTeachingPromo/useCreatePromo.ts";
import { toDayValue } from "../../UI/Utils/validityDays.ts";

type BonusType = "all_you_can_speak" | "extra_lessons" | "extra_weeks";

export type BonusFormValues = {
  type: BonusType;
  // all_you_can_speak
  lessonsCountedPerWeek?: number;
  activeFrom?: string;
  activeTo?: string;
  // extra_lessons
  extraLessons?: number;
  // extra_weeks
  extraWeeks?: number;
};

export type CreatePromoFormValues = {
  name: string;
  description?: string;
  bonuses: BonusFormValues[];
  validFrom?: string;
  validTo?: string;
  applicableProducts?: PromoApplicableProductDTO[];
};

const BONUS_LABELS: Record<BonusType, string> = {
  all_you_can_speak: "All You Can Speak",
  extra_lessons: "Lezioni Extra",
  extra_weeks: "Settimane Extra",
};

const BONUS_DESCRIPTIONS: Record<BonusType, string> = {
  all_you_can_speak:
    "Dalle lezioni conteggiate in poi, i partecipanti possono prenotare liberamente fino a domenica.",
  extra_lessons:
    "Aggiunge un numero di lezioni extra al totale del piano acquistato.",
  extra_weeks:
    "Estende la durata del piano acquistato di un numero di settimane aggiuntive.",
};

const bonusSchema = yup.object({
  type: yup
    .string()
    .oneOf(["all_you_can_speak", "extra_lessons", "extra_weeks"] as const)
    .required("Selezionare un tipo di bonus"),
  lessonsCountedPerWeek: yup.number().when("type", {
    is: "all_you_can_speak",
    then: (s) =>
      s
        .required("Inserire il numero di lezioni conteggiate per settimana")
        .min(0, "Deve essere almeno 0 (0 = illimitato)")
        .integer("Deve essere un numero intero")
        .typeError("Inserire un numero valido"),
    otherwise: (s) =>
      s
        .optional()
        .nullable()
        .transform(() => undefined),
  }),
  activeFrom: yup
    .string()
    .optional()
    .nullable()
    .transform((v, o) => (o === "" ? undefined : v)),
  activeTo: yup
    .string()
    .optional()
    .nullable()
    .transform((v, o) => (o === "" ? undefined : v))
    .test(
      "is-after-active-from",
      "La data di fine deve essere successiva alla data di inizio",
      function (value) {
        if (this.parent.type !== "all_you_can_speak") return true;
        const { activeFrom } = this.parent;
        if (!value || !activeFrom) return true;
        return new Date(value) >= new Date(activeFrom);
      },
    ),
  extraLessons: yup.number().when("type", {
    is: "extra_lessons",
    then: (s) =>
      s
        .required("Inserire il numero di lezioni extra")
        .min(1, "Deve essere almeno 1")
        .integer("Deve essere un numero intero")
        .typeError("Inserire un numero valido"),
    otherwise: (s) =>
      s
        .optional()
        .nullable()
        .transform(() => undefined),
  }),
  extraWeeks: yup.number().when("type", {
    is: "extra_weeks",
    then: (s) =>
      s
        .required("Inserire il numero di settimane extra")
        .min(1, "Deve essere almeno 1")
        .integer("Deve essere un numero intero")
        .typeError("Inserire un numero valido"),
    otherwise: (s) =>
      s
        .optional()
        .nullable()
        .transform(() => undefined),
  }),
});

const validationSchema = yup.object({
  name: yup.string().required("Inserire un nome per la promo"),
  description: yup.string().optional(),
  bonuses: yup
    .array()
    .of(bonusSchema)
    .min(1, "Aggiungere almeno un bonus")
    .required(),
  validFrom: yup
    .string()
    .optional()
    .nullable()
    .transform((v, o) => (o === "" ? undefined : v)),
  validTo: yup
    .string()
    .optional()
    .nullable()
    .transform((v, o) => (o === "" ? undefined : v))
    .test(
      "is-after-valid-from",
      "La data di fine vendita deve essere successiva alla data di inizio",
      function (value) {
        const { validFrom } = this.parent;
        if (!value || !validFrom) return true;
        return new Date(value) >= new Date(validFrom);
      },
    ),
  applicableProducts: yup
    .array()
    .of(
      yup.object({
        itemType: yup
          .string()
          .oneOf(Object.values(NormasTeachingItemType))
          .required(),
        id: yup.string().required(),
      }),
    )
    .optional(),
});

function InputWithLabel({
  children,
  label,
}: PropsWithChildren<{ label: string }>) {
  return (
    <div className="flex flex-col gap-1 flex-1">
      <label className="font-medium">{label}</label>
      {children}
    </div>
  );
}

function ApplicableProductsModal({
  isOpen,
  onClose,
  products,
  selectedItems,
  onSave,
}: {
  isOpen: boolean;
  onClose: () => void;
  products: NormasTeachingProductDTO[];
  selectedItems: PromoApplicableProductDTO[];
  onSave: (items: PromoApplicableProductDTO[]) => void;
}) {
  const [localItems, setLocalItems] = useState<PromoApplicableProductDTO[]>([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLocalItems(selectedItems);
  }, [selectedItems, isOpen]);

  useEffect(() => {
    if (isOpen) setSearch("");
  }, [isOpen]);

  const visibleProducts = useMemo(
    () =>
      products.filter((product) =>
        matchesItemSearch(search, [
          product._id,
          product.productName,
          product.variantProductName,
        ]),
      ),
    [products, search],
  );

  if (!isOpen) return null;

  const isSelected = (id: string) => localItems.some((item) => item.id === id);

  const toggleItem = (id: string) => {
    if (isSelected(id)) {
      setLocalItems(localItems.filter((item) => item.id !== id));
    } else {
      setLocalItems([
        ...localItems,
        { id, itemType: NormasTeachingItemType.Product },
      ]);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-xl font-bold">Seleziona Prodotti Applicabili</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6 pt-4">
          <TextInput
            placeholder="Cerca per nome, variante o ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-2">
          {localItems.length > 0 && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg flex items-center justify-between">
              <span className="text-sm font-medium text-blue-800">
                {localItems.length} prodotti selezionati
              </span>
              <button
                onClick={() => setLocalItems([])}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Svuota selezione
              </button>
            </div>
          )}
          {visibleProducts.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              {search.trim()
                ? "Nessun prodotto corrisponde alla ricerca"
                : "Nessun prodotto disponibile"}
            </p>
          ) : (
            visibleProducts.map((product) => (
              <div
                key={product._id}
                onClick={() => toggleItem(product._id)}
                className={`p-3 border rounded-lg cursor-pointer transition ${
                  isSelected(product._id)
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      isSelected(product._id)
                        ? "border-blue-500 bg-blue-500"
                        : "border-gray-300"
                    }`}
                  >
                    {isSelected(product._id) && (
                      <svg
                        className="w-3 h-3 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{product.productName}</p>
                    <p className="text-sm text-gray-500">
                      {product.variantProductName} — €
                      {(product.priceInCents.amount / 100).toFixed(2)}
                    </p>
                    <p className="text-xs text-gray-400 font-mono truncate">
                      {product._id}
                    </p>
                  </div>
                  <Package size={18} className="text-gray-400" />
                </div>
              </div>
            ))
          )}
        </div>

        <div className="p-6 border-t flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
          >
            Annulla
          </button>
          <PrimaryButton
            label="Salva"
            onClick={() => {
              onSave(localItems);
              onClose();
            }}
          />
        </div>
      </div>
    </div>
  );
}

function AllYouCanSpeakBonusFields({
  index,
  register,
  errors,
  lessonsCountedPerWeek,
}: {
  index: number;
  register: ReturnType<typeof useForm<CreatePromoFormValues>>["register"];
  errors: ReturnType<
    typeof useForm<CreatePromoFormValues>
  >["formState"]["errors"];
  lessonsCountedPerWeek?: number;
}) {
  return (
    <>
      <InputWithLabel label="Lezioni conteggiate per settimana">
        <TextInput
          type="number"
          min={0}
          {...register(`bonuses.${index}.lessonsCountedPerWeek`)}
          errorMessage={
            errors?.bonuses?.[index]?.lessonsCountedPerWeek?.message
          }
          placeholder="es. 2"
        />
        {lessonsCountedPerWeek === 0 && (
          <div className="mt-2 flex items-start gap-2 rounded-lg border border-yellow-300 bg-yellow-50 px-3 py-2 text-xs text-yellow-800">
            <span className="mt-0.5 text-yellow-500">⚠</span>
            <span>
              Con 0 lezioni conteggiate il piano diventa{" "}
              <strong>illimitato</strong>: l'utente potrà prenotare senza limiti
              per tutta la durata del bonus e non vedrà il contatore delle
              lezioni rimanenti.
            </span>
          </div>
        )}
      </InputWithLabel>

      <div className="flex flex-col md:flex-row gap-4">
        <InputWithLabel label="Bonus attivo da (opzionale)">
          <input
            type="date"
            {...register(`bonuses.${index}.activeFrom`)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {errors?.bonuses?.[index]?.activeFrom && (
            <span className="text-sm text-red-600">
              {errors.bonuses[index].activeFrom?.message}
            </span>
          )}
          <span className="text-xs text-gray-500">
            Lascia vuoto per nessun limite iniziale
          </span>
        </InputWithLabel>

        <InputWithLabel label="Bonus attivo fino a (opzionale)">
          <input
            type="date"
            {...register(`bonuses.${index}.activeTo`)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {errors?.bonuses?.[index]?.activeTo && (
            <span className="text-sm text-red-600">
              {errors.bonuses[index].activeTo?.message}
            </span>
          )}
          <span className="text-xs text-gray-500">
            Lascia vuoto per nessun limite finale
          </span>
        </InputWithLabel>
      </div>
    </>
  );
}

function ExtraLessonsBonusFields({
  index,
  register,
  errors,
}: {
  index: number;
  register: ReturnType<typeof useForm<CreatePromoFormValues>>["register"];
  errors: ReturnType<
    typeof useForm<CreatePromoFormValues>
  >["formState"]["errors"];
}) {
  return (
    <InputWithLabel label="Numero di lezioni extra">
      <TextInput
        type="number"
        min={1}
        {...register(`bonuses.${index}.extraLessons`)}
        errorMessage={errors?.bonuses?.[index]?.extraLessons?.message}
        placeholder="es. 10"
      />
    </InputWithLabel>
  );
}

function ExtraWeeksBonusFields({
  index,
  register,
  errors,
}: {
  index: number;
  register: ReturnType<typeof useForm<CreatePromoFormValues>>["register"];
  errors: ReturnType<
    typeof useForm<CreatePromoFormValues>
  >["formState"]["errors"];
}) {
  return (
    <InputWithLabel label="Numero di settimane extra">
      <TextInput
        type="number"
        min={1}
        {...register(`bonuses.${index}.extraWeeks`)}
        errorMessage={errors?.bonuses?.[index]?.extraWeeks?.message}
        placeholder="es. 4"
      />
    </InputWithLabel>
  );
}

function mapInitialBonus(b: PromoDTO["bonuses"][number]): BonusFormValues {
  if (b.type === "extra_lessons")
    return { type: "extra_lessons", extraLessons: b.extraLessons };
  if (b.type === "extra_weeks")
    return { type: "extra_weeks", extraWeeks: b.extraWeeks };
  return {
    type: "all_you_can_speak",
    lessonsCountedPerWeek: b.lessonsCountedPerWeek,
    activeFrom: toDayValue(b.activeFrom),
    activeTo: toDayValue(b.activeTo),
  };
}

export function mapBonusToPayload(b: BonusFormValues): CreateBonusInput {
  if (b.type === "extra_lessons")
    return { type: "extra_lessons", extraLessons: b.extraLessons! };
  if (b.type === "extra_weeks")
    return { type: "extra_weeks", extraWeeks: b.extraWeeks! };
  return {
    type: "all_you_can_speak",
    lessonsCountedPerWeek: b.lessonsCountedPerWeek!,
    activeFrom: b.activeFrom ? dayjs(b.activeFrom).toISOString() : undefined,
    activeTo: b.activeTo ? dayjs(b.activeTo).toISOString() : undefined,
  };
}

export function CreatePromoForm({
  onSubmit,
  saving,
  initialPromo,
  isEditMode = false,
  products,
}: {
  onSubmit: (data: CreatePromoFormValues) => void;
  saving: boolean;
  initialPromo?: PromoDTO;
  isEditMode?: boolean;
  products: NormasTeachingProductDTO[];
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
  } = useForm<CreatePromoFormValues>({
    resolver: yupResolver(validationSchema) as Resolver<CreatePromoFormValues>,
    defaultValues: initialPromo
      ? {
          name: initialPromo.name,
          description: initialPromo.description,
          bonuses: initialPromo.bonuses.map(mapInitialBonus),
          validFrom: toDayValue(initialPromo.validFrom),
          validTo: toDayValue(initialPromo.validTo),
          applicableProducts: initialPromo.applicableProducts ?? undefined,
        }
      : {
          bonuses: [
            {
              type: "all_you_can_speak",
              lessonsCountedPerWeek: undefined as unknown as number,
            },
          ],
          applicableProducts: undefined,
        },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "bonuses",
  });

  const watchedBonuses = watch("bonuses");
  const applicableProducts = watch("applicableProducts");

  const handleTypeChange = (index: number, newType: BonusType) => {
    setValue(`bonuses.${index}.type`, newType);
    setValue(`bonuses.${index}.lessonsCountedPerWeek`, undefined);
    setValue(`bonuses.${index}.activeFrom`, undefined);
    setValue(`bonuses.${index}.activeTo`, undefined);
    setValue(`bonuses.${index}.extraLessons`, undefined);
    setValue(`bonuses.${index}.extraWeeks`, undefined);
  };

  const getProductName = (id: string): string => {
    const product = products.find((p) => p._id === id);
    return product
      ? `${product.productName} — ${product.variantProductName}`
      : "Prodotto sconosciuto";
  };

  const onSubmitInternal = (data: CreatePromoFormValues) => {
    onSubmit({
      ...data,
      applicableProducts:
        data.applicableProducts && data.applicableProducts.length > 0
          ? data.applicableProducts
          : undefined,
    });
  };

  return (
    <form className="flex flex-col gap-6">
      <InputWithLabel label="Nome promo">
        <TextInput
          {...register("name")}
          errorMessage={errors?.name?.message}
          placeholder="es. Promo Estate 2025"
        />
      </InputWithLabel>

      <InputWithLabel label="Descrizione (opzionale)">
        <TextInput
          {...register("description")}
          errorMessage={errors?.description?.message}
          placeholder="es. Acquistando questo corso riceverai accesso illimitato per i primi 3 mesi"
        />
      </InputWithLabel>

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-800">Bonus</p>
            <p className="text-sm text-gray-600">
              Aggiungi uno o più bonus che si attivano per gli acquirenti di
              questa promo.
            </p>
          </div>
          <OutlinedButton
            iconLeft={<Plus size={18} />}
            label="Aggiungi bonus"
            onClick={() =>
              append({
                type: "all_you_can_speak",
                lessonsCountedPerWeek: undefined as unknown as number,
              })
            }
          />
        </div>

        {errors?.bonuses?.message && (
          <span className="text-sm text-red-600">{errors.bonuses.message}</span>
        )}

        {fields.map((field, index) => {
          const currentType = watchedBonuses?.[index]?.type ?? field.type;
          return (
            <div
              key={field.id}
              className="border rounded-lg p-4 bg-gray-50 flex flex-col gap-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 mr-4">
                  <Controller
                    control={control}
                    name={`bonuses.${index}.type`}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onChange={(newType) =>
                          handleTypeChange(index, newType as BonusType)
                        }
                        options={(
                          Object.entries(BONUS_LABELS) as [BonusType, string][]
                        ).map(([value, label]) => ({ id: value, name: label }))}
                      />
                    )}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {BONUS_DESCRIPTIONS[currentType]}
                  </p>
                </div>
                {fields.length > 1 && (
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded transition flex-shrink-0"
                    title="Rimuovi bonus"
                  >
                    <Trash2 size={18} />
                  </button>
                )}
              </div>

              {currentType === "all_you_can_speak" && (
                <AllYouCanSpeakBonusFields
                  index={index}
                  register={register}
                  errors={errors}
                  lessonsCountedPerWeek={
                    watchedBonuses?.[index]?.lessonsCountedPerWeek
                  }
                />
              )}
              {currentType === "extra_lessons" && (
                <ExtraLessonsBonusFields
                  index={index}
                  register={register}
                  errors={errors}
                />
              )}
              {currentType === "extra_weeks" && (
                <ExtraWeeksBonusFields
                  index={index}
                  register={register}
                  errors={errors}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <InputWithLabel label="Promo in vendita da (opzionale)">
          <input
            type="date"
            {...register("validFrom")}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {errors?.validFrom && (
            <span className="text-sm text-red-600">
              {errors.validFrom.message}
            </span>
          )}
          <span className="text-xs text-gray-500">
            Lascia vuoto per nessun limite iniziale
          </span>
        </InputWithLabel>

        <InputWithLabel label="Promo in vendita fino a (opzionale)">
          <input
            type="date"
            {...register("validTo")}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {errors?.validTo && (
            <span className="text-sm text-red-600">
              {errors.validTo.message}
            </span>
          )}
          <span className="text-xs text-gray-500">
            Lascia vuoto per nessun limite finale
          </span>
        </InputWithLabel>
      </div>

      <div className="border rounded-lg p-4 bg-gray-50">
        <div className="flex items-center justify-between mb-3">
          <div>
            <label className="font-medium text-gray-800">
              Prodotti Applicabili
            </label>
            <p className="text-sm text-gray-600">
              Seleziona i prodotti a cui questa promo è applicabile. Lascia
              vuoto per applicarla a tutti.
            </p>
          </div>
          <OutlinedButton
            iconLeft={<Plus size={18} />}
            label="Seleziona"
            onClick={() => setIsModalOpen(true)}
          />
        </div>

        {applicableProducts && applicableProducts.length > 0 ? (
          <div className="bg-white rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                {applicableProducts.length} prodotti selezionati
              </span>
              <button
                type="button"
                onClick={() => setValue("applicableProducts", undefined)}
                className="text-sm text-red-600 hover:text-red-800"
              >
                Rimuovi tutti
              </button>
            </div>
            {applicableProducts.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-2 bg-gray-50 rounded"
              >
                <div className="flex items-center gap-2">
                  <Package size={16} className="text-blue-500" />
                  <span className="text-sm">{getProductName(item.id)}</span>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setValue(
                      "applicableProducts",
                      applicableProducts.filter((i) => i.id !== item.id),
                    )
                  }
                  className="p-1 text-red-600 hover:bg-red-50 rounded transition"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 bg-white rounded-lg border-2 border-dashed border-gray-300">
            <p className="text-gray-500 text-sm">
              Nessun prodotto selezionato. La promo sarà applicabile a tutti i
              prodotti.
            </p>
          </div>
        )}
      </div>

      <PrimaryButton
        loading={saving}
        label={isEditMode ? "Aggiorna Promo" : "Crea Promo"}
        onClick={handleSubmit(onSubmitInternal)}
      />

      <ApplicableProductsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        products={products}
        selectedItems={applicableProducts ?? []}
        onSave={(items) =>
          setValue("applicableProducts", items.length > 0 ? items : undefined)
        }
      />
    </form>
  );
}
